import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AdminUser, User, UserRole } from '../models/user.model';
import { ApiDefaultResponse } from './auth.service';

export interface ApiUser {
  _id?: string;
  id?: string;
  auth?: {
    type?: string;
    email?: string;
    googleId?: string;
  };
  email?: string;
  contactEmail?: string;
  username?: string;
  name?: string;
  role?: UserRole | string;
  firstName?: string;
  lastName?: string;
  profileImage?: {
    thumb?: string;
    small?: string;
  } | null;
  avatar?: string;
  createDate?: string;
  createdAt?: string;
  lastLogin?: string;
  phone?: string;
  emailConfirmed?: boolean;
  status?: string;
  location?: string;
  city?: string;
  petsCount?: number;
  pets?: unknown[];
  totalSpent?: number;
  totalSpentAmount?: number;
  countryAccess?: string[];
  [key: string]: any;
}

export interface UsersResponse {
  data: {
    users: ApiUser[];
    pagination: {
      page: number;
      perPage: number;
      total: number;
      totalPages: number;
    };
    statistics: {
      byRole: {
        ADMIN?: number;
        NORMAL?: number;
      };
      byBusiness: {
        withBusiness?: number;
        withoutBusiness?: number;
      };
      total: number;
    };
  };
}

export interface UsersFilter {
  role?: 'ADMIN' | 'NORMAL';
  status?: string;
  search?: string;
  countryCode?: string;
  sortBy?: string;
  orderBy?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  getUsers(filter: UsersFilter = {}): Observable<UsersResponse> {
    let params = new HttpParams();

    const page = filter.page !== undefined ? filter.page : 0;
    const perPage = filter.perPage !== undefined ? filter.perPage : 10;
    params = params.set('page', page.toString()).set('perPage', perPage.toString());

    if (filter.role) {
      params = params.set('role', filter.role);
    }

    if (filter.status) {
      params = params.set('status', filter.status);
    }

    if (filter.search) {
      params = params.set('search', filter.search);
    }

    if (filter.countryCode) {
      params = params.set('countryCode', filter.countryCode);
    }

    if (filter.sortBy) {
      params = params.set('sortBy', filter.sortBy);
    }

    if (filter.orderBy) {
      params = params.set('orderBy', filter.orderBy);
    }

    return this.http.get<UsersResponse>(`${this.apiUrl}/v1/users/admin/list`, {
      params,
    });
  }

  getUserById(id: string): Observable<User> {
    return this.http
      .get<ApiDefaultResponse<User>>(`${this.apiUrl}/v1/users/${id}`)
      .pipe(map((response) => response.data));
  }

  getUserAdminStatsById(id: string): Observable<User> {
    return this.http
      .get<ApiDefaultResponse<User>>(`${this.apiUrl}/v1/users/admin/${id}/stats`)
      .pipe(map((response) => response.data));
  }

  deleteUser(userId: string): Observable<unknown> {
    return this.http.delete<unknown>(`${this.apiUrl}/v1/users/${userId}`);
  }

  deleteManyUsers(userIds: string[]): Observable<unknown> {
    if (userIds.length === 0) {
      return of({});
    }
    const deleteObservables = userIds.map((userId) => this.deleteUser(userId));
    return forkJoin(deleteObservables);
  }

  updateUser(data: Partial<User>, userId: string): Observable<User> {
    return this.http
      .put<ApiDefaultResponse<User>>(`${this.apiUrl}/v1/users/${userId}`, data)
      .pipe(map((res) => res.data));
  }

  blockUser(userId: string): Observable<AdminUser> {
    return this.http
      .post<ApiDefaultResponse<AdminUser>>(`${this.apiUrl}/v1/users/admin/${userId}/block`, {})
      .pipe(map((res) => res.data));
  }

  unblockUser(userId: string): Observable<AdminUser> {
    return this.http
      .post<ApiDefaultResponse<AdminUser>>(`${this.apiUrl}/v1/users/admin/${userId}/unblock`, {})
      .pipe(map((res) => res.data));
  }

  createAdmin(payload: {
    email: string;
    username?: string;
    name?: string;
    countryAccess?: string[];
  }): Observable<ApiUser> {
    return this.http
      .post<ApiDefaultResponse<ApiUser>>(`${this.apiUrl}/v1/users/admin`, payload)
      .pipe(map((res) => res.data));
  }

  updateAdmin(
    userId: string,
    payload: { countryAccess?: string[]; role?: string },
  ): Observable<ApiUser> {
    return this.http
      .put<ApiDefaultResponse<ApiUser>>(`${this.apiUrl}/v1/users/admin/${userId}`, payload)
      .pipe(map((res) => res.data));
  }

  getAdminStats(countryCode?: string): Observable<any> {
    let params = new HttpParams();
    if (countryCode) {
      params = params.set('countryCode', countryCode);
    }
    return this.http.get<any>(`${this.apiUrl}/v1/users/admin/stats`, { params }).pipe(map((res) => res.data));
  }

  getRegistrationGraph(countryCode?: string): Observable<any> {
    let params = new HttpParams();
    if (countryCode) {
      params = params.set('countryCode', countryCode);
    }
    return this.http
      .get<any>(`${this.apiUrl}/v1/users/admin/registration-graph`, { params })
      .pipe(map((res) => res.data));
  }
}
